import React, { useEffect, useMemo, useState } from 'react';
import { VStack, HStack, Text } from 'native-base';
import { format } from 'date-fns';
import computeTripProgress from '../../utils/tripProgress';
import { getActivePause, pauseEndOf, totalPausedMinutes } from '../../utils/tripPauses';

const DELAY_TOLERANCE_MINUTES = 5;

const formatDistance = (km) => {
  if (km < 1) return 'moins d\'1';
  return km < 10 ? km.toFixed(1) : String(Math.round(km));
};

const formatDelay = (minutes) => {
  const abs = Math.abs(minutes);
  const duration = abs >= 60 ? `${Math.floor(abs / 60)} h ${String(abs % 60).padStart(2, '0')}` : `${abs} min`;
  if (minutes >= DELAY_TOLERANCE_MINUTES) return { label: `+${duration} de retard`, color: 'orange.600' };
  if (minutes <= -DELAY_TOLERANCE_MINUTES) return { label: `${duration} d'avance`, color: 'green.600' };
  return { label: 'À l\'heure', color: 'green.600' };
};

function Row({ label, children }) {
  return (
    <HStack justifyContent="space-between" alignItems="center">
      <Text fontSize="sm" color="gray.600">{label}</Text>
      {children}
    </HStack>
  );
}

/** Real start, estimated arrival (with the gap to the plan) and remaining distance. */
function TripProgressSummary({
  trip, routeGeometry, lastLocation, arrivalCoords,
}) {
  // Re-evaluate "is the pause still running" without waiting for new data.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);
  const pauses = trip.pauses ?? [];
  const activePause = getActivePause(pauses, now);
  const pausedMinutes = totalPausedMinutes(pauses, now);

  const progress = useMemo(() => computeTripProgress({
    routeGeometry,
    lastLocation,
    arrivalCoords,
    startedAt: trip.startedAt,
    distanceInKm: trip.distanceInKm,
    estimatedDuration: trip.estimatedDuration,
    plannedArrival: trip.arrivalDateTime,
    pauses: trip.pauses,
  }), [
    routeGeometry, lastLocation, arrivalCoords, trip.startedAt, trip.distanceInKm,
    trip.estimatedDuration, trip.arrivalDateTime, trip.pauses,
  ]);

  const delay = progress?.delayMinutes != null ? formatDelay(progress.delayMinutes) : null;

  return (
    <VStack space={1}>
      {trip.startedAt && (
        <Row label="Départ réel :">
          <Text fontSize="sm" fontWeight="bold">{format(new Date(trip.startedAt), 'HH:mm')}</Text>
        </Row>
      )}
      {progress?.estimatedArrival && (
        <Row label="Arrivée estimée :">
          <VStack alignItems="flex-end">
            <Text fontSize="sm" fontWeight="bold">
              {format(progress.estimatedArrival, 'HH:mm')}
            </Text>
            {delay && <Text fontSize="xs" color={delay.color}>{delay.label}</Text>}
          </VStack>
        </Row>
      )}
      {activePause && (
        <Row label="Pause :">
          <Text fontSize="sm" fontWeight="bold" color="orange.600">
            {`en cours jusqu'à ${format(new Date(pauseEndOf(activePause)), 'HH:mm')}`}
          </Text>
        </Row>
      )}
      {pauses.length > 0 && (
        <Row label="Pauses effectuées :">
          <Text fontSize="sm">{`${pauses.length} · ${pausedMinutes} min`}</Text>
        </Row>
      )}
      {progress && (
        <Row label="Distance restante :">
          <Text fontSize="sm" fontWeight="bold">
            {`${progress.approximate ? '≈ ' : ''}${formatDistance(progress.remainingKm)} km`}
          </Text>
        </Row>
      )}
    </VStack>
  );
}

export default TripProgressSummary;
